// Bulles d'aide du labyrinthe : une phrase à la fois, posée à côté du personnage qu'elle décrit
// (souris, fromage, rat), qui défilent en boucle jusqu'à stop(). Une seule bulle visible évite
// qu'elles se chevauchent sur les petits labyrinthes.

import { keepPunctuationAttached, setTextWithEmoji } from './dom.js';

const GAP_PX = 8;  // espace entre la case et la bulle (la pointe s'y loge)
const EDGE_PX = 4; // marge minimale avec le bord du labyrinthe

/**
 * Où poser la bulle d'une case : en dessous si la case est dans la moitié haute (au-dessus sinon),
 * et alignée sur le bord de la case côté intérieur quand elle est près d'un bord, pour ne jamais
 * sortir du labyrinthe.
 * @returns {{ side: 'above'|'below', align: 'start'|'center'|'end' }}
 */
export function bubblePlacement(cell, gridSize) {
  const side = cell.y < gridSize / 2 ? 'below' : 'above';
  const column = (cell.x + 0.5) / gridSize;
  const align = column < 1 / 3 ? 'start' : column > 2 / 3 ? 'end' : 'center';
  return { side, align };
}

/**
 * @param {HTMLElement} container  conteneur positionné du canvas
 * @param {HTMLElement} el         la bulle
 * @param {object} options
 * @param {number} options.holdMs  durée d'affichage de chaque bulle
 * @param {(cell: {x:number,y:number}|null) => void} [options.onFocus]  personnage désigné (halo)
 */
export function createHintBubbles(container, el, { holdMs, onFocus = () => {} }) {
  let getSteps = null;
  let index = 0;
  let timer = null;

  function place(cell, gridSize) {
    const width = container.clientWidth;
    const cellPx = width / gridSize;
    const { side, align } = bubblePlacement(cell, gridSize);
    const shiftX = { start: '0%', center: '-50%', end: '-100%' }[align];
    const anchorX = {
      start: Math.max(EDGE_PX, cell.x * cellPx),
      center: (cell.x + 0.5) * cellPx,
      end: Math.min(width - EDGE_PX, (cell.x + 1) * cellPx),
    }[align];
    // Pointe face au centre de la case, quel que soit le décalage de la bulle
    const toCenter = Math.abs((cell.x + 0.5) * cellPx - anchorX);
    const arrowX = { start: `${toCenter}px`, center: '50%', end: `calc(100% - ${toCenter}px)` }[align];
    el.dataset.side = side;
    el.style.left = `${anchorX}px`;
    el.style.top = side === 'below' ? `${(cell.y + 1) * cellPx + GAP_PX}px` : `${cell.y * cellPx - GAP_PX}px`;
    el.style.transform = `translate(${shiftX}, ${side === 'below' ? '0' : '-100%'})`;
    el.style.setProperty('--arrow-x', arrowX);
  }

  /** Affiche la bulle courante (textes et positions relus : langue ou taille ont pu changer). */
  function render() {
    const steps = getSteps ? getSteps() : [];
    const step = steps[index % steps.length];
    if (!step) return;
    setTextWithEmoji(el, keepPunctuationAttached(step.text));
    place(step.cell, step.gridSize);
    el.classList.add('is-visible');
    onFocus(step.cell);
  }

  function schedule() {
    timer = setTimeout(() => {
      index++;
      render();
      schedule();
    }, holdMs);
  }

  /** @param {() => {text:string, cell:{x:number,y:number}, gridSize:number}[]} stepsProvider */
  function start(stepsProvider) {
    stop();
    getSteps = stepsProvider;
    index = 0;
    render();
    schedule();
  }

  function stop() {
    if (timer) clearTimeout(timer);
    timer = null;
    if (!getSteps) return;
    getSteps = null;
    el.classList.remove('is-visible');
    onFocus(null);
  }

  return {
    start,
    stop,
    refresh: () => { if (getSteps) render(); },
    isActive: () => getSteps !== null,
  };
}
